// The `media-upload` and `video-upload` lanes' names, the frozen row payload they share and the pure builder for their
// queue items (BL-2679, BL-2684). The page's hand-off (`stores/change-queue/lot-media-native.ts`) is the caller.
import { LOT_MEDIA_INGEST_ID_PATTERN, LOT_MEDIA_INGEST_LIMITS } from '@auctogon/core-admin/schemas/lot-attachments';

import type { NativeTransferItem } from './types';

/** The native lane that uploads a lot's photos in the background (ADR 070 — Ingest Protocol § 4). */
export const MEDIA_UPLOAD_LANE = 'media-upload';

/** The native lane that uploads a lot's videos in the background (BL-2684): the same chain, a lane name an older build lacks. */
export const VIDEO_UPLOAD_LANE = 'video-upload';

/** JSON a lane stores in its row payload; the named type keeps `unknown` out of the contract. */
export type MediaUploadJson = string | number | boolean | null | MediaUploadJson[] | { [key: string]: MediaUploadJson };

/**
 * The frozen `media-upload` row payload (BL-2679; the Swift twin is `UploadPayload.swift`). The last six keys
 * are written by the native lane as it advances the chain; the page never sends them.
 */
export interface MediaUploadPayload {
  kind: 'photo' | 'video';
  lotId: string;
  mediaId: string;
  photoStagedId: string;
  thumbStagedId: string;
  contentType: string;
  position: number;
  isCover: boolean;
  width?: number;
  height?: number;
  /** Chain mode only: the storage entity the signed-link request names (`inventory` and the lot's item id; a video always runs the chain). */
  linkEntityType?: string;
  linkEntityId?: string;
  thumbnailMaxBytes: number;
  chainThresholdBytes: number;
  step?: string;
  signedUrl?: string;
  uploadHeaders?: { [header: string]: string };
  storagePath?: string;
  /** The thumbnail's own signed link and headers (BL-2884); the photo's are `signedUrl` / `uploadHeaders`. */
  thumbSignedUrl?: string;
  thumbUploadHeaders?: { [header: string]: string };
}

/** What the page supplies: the payload without the native-written keys and without the two server limits. */
export type MediaUploadPayloadInput = Omit<
  MediaUploadPayload,
  'step' | 'signedUrl' | 'uploadHeaders' | 'storagePath' | 'thumbSignedUrl' | 'thumbUploadHeaders' | 'thumbnailMaxBytes' | 'chainThresholdBytes'
>;

/** Input to {@link toMediaUploadItem}. */
export interface MediaUploadItemInput {
  /** Text captured when the row was queued (ADR 066 § 8). */
  label: string;
  payload: MediaUploadPayloadInput;
}

/**
 * Builds the queue item for one photo or video (the lane follows `payload.kind`): row id = media id, grouped by lot, naming both staged files, with the
 * two limits stamped from the server's own constant so the native checks never drift from the ingest route.
 * @param input - the label and the page-supplied payload.
 * @returns the item to enqueue on the `media-upload` (photo) or `video-upload` (video) lane.
 * @throws Error when `mediaId` is not a lowercase UUID v4 (the id is phone-minted and goes in the URL path).
 */
export function toMediaUploadItem(input: MediaUploadItemInput): NativeTransferItem {
  const { label, payload } = input;
  if (!LOT_MEDIA_INGEST_ID_PATTERN.test(payload.mediaId)) {
    throw new Error(`media-upload: mediaId must be a lowercase UUID v4, got "${payload.mediaId}"`);
  }
  const full: MediaUploadPayload = {
    ...payload,
    thumbnailMaxBytes: LOT_MEDIA_INGEST_LIMITS.thumbnailBytes,
    chainThresholdBytes: LOT_MEDIA_INGEST_LIMITS.nativeThresholdBytes,
  };
  return {
    id: payload.mediaId,
    lane: payload.kind === 'video' ? VIDEO_UPLOAD_LANE : MEDIA_UPLOAD_LANE,
    lotId: payload.lotId,
    label,
    payload: full as unknown as { [key: string]: MediaUploadJson }, // optional keys read as undefined, not JSON
    stagedIds: [payload.photoStagedId, payload.thumbStagedId],
  };
}
